import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { CustomEventCollectionPageViewSettingsDto } from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { AddAnotherButton } from "@src/components/Utils/AddAnotherButton";
import { CollectionListItem } from "@src/features/customEvents/components/subpages/CollectionListItem";
import { useFetchCollectionsAllQuery } from "@src/generated";
import {
  selectedCurrentCustomEvent,
  setCustomEventCollections,
} from "@src/store/slices/customEvents";

interface CollectionPageViewProps {
  isArchived: boolean;
}

export const CollectionPageView = ({ isArchived }: CollectionPageViewProps) => {
  const dispatch = useDispatch();
  const currentEvent = useSelector(selectedCurrentCustomEvent);
  const collectionsQuery = useFetchCollectionsAllQuery();
  const [selectedCollections, setSelectedCollections] = useImmer<
    (string | null)[]
  >(
    currentEvent?.id && currentEvent?.type === "collectionPageView"
      ? currentEvent.settings.map((event) => event.handle)
      : [null]
  );

  const collectionOptions =
    collectionsQuery?.data?.collections?.edges?.map((edge) => edge.node) || [];

  const selectedCollectionsToSettingsDto = (
    selectedCollections: (string | null)[]
  ): CustomEventCollectionPageViewSettingsDto => {
    return selectedCollections
      .filter((collection) => collection !== null)
      .map((collection) => ({ handle: collection }));
  };

  const handleAddCollection = () => {
    setSelectedCollections((draft) => {
      draft.push(null); // placeholder for a new collection selection
    });
  };

  const handleCollectionChange = (index: number, newValue: string | null) => {
    setSelectedCollections((draft) => {
      draft[index] = newValue;
    });
  };

  const handleRemoveCollection = (index: number) => {
    setSelectedCollections((draft) => {
      draft.splice(index, 1);
    });
  };

  useEffect(() => {
    dispatch(
      setCustomEventCollections({
        collections: selectedCollectionsToSettingsDto(selectedCollections),
      })
    );
  }, [selectedCollections]);

  if (!collectionsQuery?.data?.collections?.edges?.length) {
    return <div>Loading collections...</div>;
  }

  return (
    <Grid item width="100%" marginTop="15px">
      {selectedCollections.map((selectedCollection, index) => (
        <CollectionListItem
          key={index}
          selectedCollection={selectedCollection}
          selectedCollections={selectedCollections}
          options={collectionOptions}
          isArchived={isArchived}
          onChange={(newValue) =>
            handleCollectionChange(index, newValue?.handle || null)
          }
          onRemove={() => handleRemoveCollection(index)}
        />
      ))}
      {!isArchived && (
        <AddAnotherButton
          onAddClick={handleAddCollection}
          itemLabel={"add another collection"}
        />
      )}
    </Grid>
  );
};
